import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { SequenceRenderCfg, WeatherKey } from "./IslandRegistry";
import { getScoreColorFromVisual } from "./questionnaireCardUtils";

type Props = {
  data: { date: Date; score: number }[];
  renderCfg: SequenceRenderCfg;
  animateKey: number;
};

type ChartDatum = {
  date: Date;
  score: number;
  plottedScore: number;
};

const MIN_TICK_LABEL_SPACING = 100;

function selectTickDates(
  chartData: ChartDatum[],
  toX: (date: Date) => number,
  minSpacing = MIN_TICK_LABEL_SPACING
) {
  if (chartData.length <= 3) {
    return chartData.map((d) => d.date);
  }

  const firstDate = chartData[0].date;
  const lastDate = chartData[chartData.length - 1].date;
  const lastX = toX(lastDate);
  const selectedDates: Date[] = [firstDate];
  let lastSelectedX = toX(firstDate);

  for (let index = 1; index < chartData.length - 1; index += 1) {
    const currentDate = chartData[index].date;
    const currentX = toX(currentDate);

    if (currentX - lastSelectedX < minSpacing) {
      continue;
    }

    if (lastX - currentX < minSpacing) {
      continue;
    }

    selectedDates.push(currentDate);
    lastSelectedX = currentX;
  }

  selectedDates.push(lastDate);

  if (selectedDates.length >= 3) {
    return selectedDates;
  }

  const middleDate = chartData[Math.floor((chartData.length - 1) / 2)].date;
  const fallbackDates = new Map<number, Date>();

  [firstDate, middleDate, lastDate].forEach((date) => {
    fallbackDates.set(+date, date);
  });

  return Array.from(fallbackDates.values()).sort((left, right) => +left - +right);
}

/**
 * Preload + decode an image URL so SVG <image> won't "pop in" after animation starts.
 */
function usePreloadedImage(url?: string) {
  const [ready, setReady] = useState(() => !url);

  useEffect(() => {
    if (!url) {
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);

    const img = new Image();
    img.src = url;

    const done = () => {
      if (!cancelled) setReady(true);
    };

    // decode() avoids the “loaded but not decoded/paintable yet” moment
    const decode = (img as any).decode as undefined | (() => Promise<void>);
    if (typeof decode === "function") {
      decode.call(img).then(done).catch(done);
    } else {
      img.onload = done;
      img.onerror = done;
    }

    return () => {
      cancelled = true;
    };
  }, [url]);

  return ready;
}

const EvolutionChart: React.FC<Props> = ({ data, renderCfg, animateKey }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const idRef = useRef<string>("");
  if (!idRef.current) idRef.current = `evo-${Math.random().toString(36).slice(2)}`;
  const uid = idRef.current;
  const backgroundUrl = renderCfg.backgroundUrl;

  // ✅ Wait for background to be fetched + decoded before starting animation/drawing
  const bgReady = usePreloadedImage(backgroundUrl);

  useEffect(() => {
    if (!data.length || !svgRef.current) return;
    if (!bgReady) return;

    const chartData: ChartDatum[] = data.map((point) => ({
      ...point,
      plottedScore: renderCfg.graphValueTransform?.(point.score) ?? point.score,
    }));

    const width = 600;
    const height = 400;
    const margin = { top: 30, right: 20, bottom: 50, left: 60 };

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const xDomain = d3.extent(chartData, (d) => d.date) as [Date, Date];

    let yMin = d3.min(chartData, (d) => d.plottedScore)!;
    let yMax = d3.max(chartData, (d) => d.plottedScore)!;

    if (renderCfg.graphDomain) {
      yMin = renderCfg.graphDomain.min;
      yMax = renderCfg.graphDomain.max;
    } else if (renderCfg.scale) {
      yMin = renderCfg.scale.min;
      yMax = renderCfg.scale.max;
    } else {
      yMin = Math.max(0, yMin - 1);
      yMax = yMax + 2;
    }

    const x = d3
      .scaleTime()
      .domain(xDomain)
      .range([margin.left, width - margin.right]);

    const y = d3
      .scaleLinear()
      .domain([yMin, yMax])
      .range([height - margin.bottom, margin.top]);

    // Backgrounds & area
    let clipRect: d3.Selection<SVGRectElement, unknown, null, undefined> | null =
      null;

    if (data.length > 1) {
      if (backgroundUrl) {
        svg
          .append("image")
          .attr("href", backgroundUrl)
          .attr("x", margin.left)
          .attr("y", margin.top)
          .attr("width", chartWidth)
          .attr("height", chartHeight)
          .attr("preserveAspectRatio", "none")
          .attr("opacity", 0.2);

        const area = d3
          .area<ChartDatum>()
          .x((d) => x(d.date))
          .y0(height - margin.bottom)
          .y1((d) => y(d.plottedScore))
          .curve(d3.curveMonotoneX);

        const areaPathId = `area-path-${uid}`;
        const revealClipId = `reveal-clip-${uid}`;
        const areaOnlyClipId = `area-only-clip-${uid}`;

        svg
          .append("path")
          .datum(chartData)
          .attr("d", area)
          .attr("id", areaPathId)
          .attr("fill", "none");

        const defs = svg.append("defs");

        clipRect = defs
          .append("clipPath")
          .attr("id", revealClipId)
          .append<SVGRectElement>("rect")
          .attr("x", margin.left)
          .attr("y", margin.top)
          .attr("width", 0)
          .attr("height", chartHeight);

        const areaGroup = svg.append("g").attr("clip-path", `url(#${revealClipId})`);

        areaGroup
          .append("clipPath")
          .attr("id", areaOnlyClipId)
          .append("use")
          .attr("href", `#${areaPathId}`);

        areaGroup
          .append("image")
          .attr("href", backgroundUrl)
          .attr("x", margin.left)
          .attr("y", margin.top)
          .attr("width", chartWidth)
          .attr("height", chartHeight)
          .attr("preserveAspectRatio", "none")
          .attr("clip-path", `url(#${areaOnlyClipId})`)
          .attr("opacity", 1);
      }
    } else {
      if (backgroundUrl) {
        svg
          .append("image")
          .attr("href", backgroundUrl)
          .attr("x", margin.left)
          .attr("y", margin.top)
          .attr("width", chartWidth)
          .attr("height", chartHeight)
          .attr("preserveAspectRatio", "none")
          .attr("opacity", 1);
      }

      const singlePoint = chartData[0];
      svg
        .append("circle")
        .attr("cx", x(singlePoint.date))
        .attr("cy", y(singlePoint.plottedScore))
        .attr("r", 20)
        .attr("fill", getScoreColorFromVisual(singlePoint.score, renderCfg.scoreVisual))
        .attr("opacity", 0.15);
    }

    // Axes
    const formatDate = d3.timeFormat("%a %d %b");
    const tickValues = selectTickDates(chartData, (date) => x(date));

    svg
      .append("g")
      .attr("transform", `translate(0,${height - margin.bottom})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(tickValues)
          .tickFormat((d) => formatDate(d as Date))
      )
      .selectAll("text")
      .attr("text-anchor", "end")
      .attr("transform", "rotate(-30)")
      .attr("dx", "-0.6em")
      .attr("dy", "0.15em")
      .style("font-size", "11px");

    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(5))
      .style("font-size", "12px");

    // Line + points
    const line = d3
      .line<ChartDatum>()
      .x((d) => x(d.date))
      .y((d) => y(d.plottedScore))
      .curve(d3.curveMonotoneX);

    const path = svg
      .append("path")
      .datum(chartData)
      .attr("fill", "none")
      .attr("stroke", "#333")
      .attr("stroke-width", 2)
      .attr("d", line);

    // ---- points: circles | emojis | sprites ----
    let pointsSelection: d3.Selection<any, any, any, any>;

    const isEmojiMode =
      renderCfg.chartType === "emojis" && renderCfg.scale?.mode === "emoji";

    const isSpriteMode =
      renderCfg.chartType === "sprites" &&
      renderCfg.scale?.mode === "sprite" &&
      renderCfg.spriteUrls;

    if (isEmojiMode && renderCfg.scale) {
      const getEmoji = renderCfg.scale.map;
      pointsSelection = svg
        .selectAll(".emoji")
        .data(chartData)
        .enter()
        .append("text")
        .attr("class", "emoji")
        .attr("x", (d) => x(d.date))
        .attr("y", (d) => y(d.plottedScore))
        .attr("text-anchor", "middle")
        .attr("alignment-baseline", "middle")
        .attr("font-size", 31)
        .attr("opacity", 0)
        .text((d) => getEmoji(d.score));
    } else if (isSpriteMode && renderCfg.scale && renderCfg.spriteUrls) {
      const getKey = renderCfg.scale.map;
      const spriteSize = 40;

      pointsSelection = svg
        .selectAll(".sprite")
        .data(chartData)
        .enter()
        .append("image")
        .attr("class", "sprite")
        .attr("x", (d) => x(d.date) - spriteSize / 2)
        .attr("y", (d) => y(d.plottedScore) - spriteSize / 2)
        .attr("width", spriteSize)
        .attr("height", spriteSize)
        .attr("opacity", 0)
        .attr("href", (d) => {
          const key = getKey(d.score) as WeatherKey;
          return renderCfg.spriteUrls?.[key] ?? "";
        });
    } else {
      // default: circles
      pointsSelection = svg
        .selectAll(".dot")
        .data(chartData)
        .enter()
        .append("circle")
        .attr("class", "dot")
        .attr("cx", (d) => x(d.date))
        .attr("cy", (d) => y(d.plottedScore))
        .attr("r", 5)
        .attr("fill", (d) => getScoreColorFromVisual(d.score, renderCfg.scoreVisual))
        .attr("opacity", 0);
    }

    const totalLength = path.node()?.getTotalLength() || 0;
    path.attr("stroke-dasharray", totalLength).attr("stroke-dashoffset", totalLength);

    const duration = 3500;

    let lastTime = Date.now();
    let progress = 0;
    let isPaused = false;
    let pauseEnd = 0;
    const revealedPoints = new Set<number>();

    const timer = d3.timer(function tick() {
      const now = Date.now();

      if (isPaused) {
        if (now < pauseEnd) return;
        isPaused = false;
        lastTime = now;
      }

      const delta = now - lastTime;
      lastTime = now;
      progress += delta / duration;
      if (progress > 1) progress = 1;

      const currentLength = totalLength * (1 - progress);
      path.attr("stroke-dashoffset", currentLength);

      const p = path.node()?.getPointAtLength(totalLength * progress);
      if (p) {
        if (clipRect) {
          clipRect.attr("width", p.x - margin.left);
        }
        pointsSelection.each(function (d: ChartDatum) {
          const el = d3.select(this);
          const key = +d.date;
          if (x(d.date) <= p.x && !revealedPoints.has(key)) {
            revealedPoints.add(key);

            if (isEmojiMode) {
              el
                .transition()
                .duration(250)
                .attr("opacity", 1)
                .attr("font-size", 40)
                .transition()
                .duration(150)
                .attr("font-size", 31);
            } else if (isSpriteMode) {
              el.transition().duration(250).attr("opacity", 1).transition().duration(150);
            } else {
              el
                .transition()
                .duration(250)
                .attr("opacity", 1)
                .attr("r", 8)
                .transition()
                .duration(150)
                .attr("r", 5);
            }

            isPaused = true;
            pauseEnd = now + 400;
          }
        });
      }

      if (progress >= 1) return true;
    });

    return () => {
      timer.stop();
      svg.selectAll("*").interrupt();
      svg.selectAll("*").remove();
    };
  }, [data, renderCfg, animateKey, bgReady]);

  return (
    <div style={{ textAlign: "center", width: "100%", position: "relative" }}>
      {/* optional placeholder while the bg is decoding */}
      {!bgReady && (
        <div style={{ width: "100%", maxWidth: 600, height: 400, opacity: 0.08 }} />
      )}

      <svg
        ref={svgRef}
        className="evo-chart"
        viewBox="0 0 600 400"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: "100%", height: "auto", maxWidth: "600px" }}
      />
    </div>
  );
};

export default React.memo(EvolutionChart);